import { computed, ComputedRef, ref } from 'vue'
import { defineStore } from 'pinia'
import { PayrollModel } from '../models/PayrollModel'
export type PayrollWindow = {
  start: Date
  end: Date
  approval_cutoff_date: Date
  last_start: Date
  last_end: Date
  last_approval_cutoff_date: Date
}
type UsePayrollWindowReturn = {
  current: ComputedRef<PayrollWindow>
  requestPayrollData: () => boolean
  setPayroll: (data: any, options?: { write?: boolean }) => Promise<void>
  resetStoreState: () => void
  /**check if a date is within current pay period */
  withinCurrentPayPeriod: (date: Date) => boolean
  /**Check if a date is pass the payroll period end (on device) */
  pastPayrollEnd: (date: Date) => boolean
  /**Check if a date (namely a timecard) is past the current payroll window.
   * Supports 2 scenarios.  1) the payroll window has synced and the date is past
   * the payroll window or 2) it has not synced, but the current date is past the payroll
   * This function takes in a date in this case just the timesheet date) and
   * determines if it is out of the payroll window
   */
  pastPayroll: (date: Date) => boolean
}
export const usePayrollWindow = defineStore('payroll', (): UsePayrollWindowReturn => {
  const currentPayrollWindow = ref()
  //US payroll window for neptune is 1 week (Mon -> Sun), CA is 2 weeks
  const current = computed(() => currentPayrollWindow.value)

  const requestPayrollData = () => {
    const now = new Date()

    if (!currentPayrollWindow.value) return true
    else if (!currentPayrollWindow.value.last_start) return true
    else if (
      new Date(currentPayrollWindow.value.start).getTime() <= now.getTime() &&
      new Date(currentPayrollWindow.value.end).getTime() >= now.getTime()
    )
      return false
    else return true
  }

  const setPayroll = async (data: any, options: { write?: boolean } = { write: true }) => {
    currentPayrollWindow.value = data
    if (options.write) {
      await PayrollModel().write(currentPayrollWindow.value)
    }
  }

  const resetStoreState = () => {
    void setPayroll(undefined, { write: false })
  }

  const withinCurrentPayPeriod = (date: Date) => {
    if (!currentPayrollWindow.value) return true
    else if (
      new Date(currentPayrollWindow.value.start).getTime() <= date.getTime() &&
      new Date(currentPayrollWindow.value.end).getTime() >= date.getTime()
    ) {
      return true
    } else return false
  }
  const pastPayrollEnd = (date: Date) => {
    if (!currentPayrollWindow.value) return false
    else return new Date(currentPayrollWindow.value.end).getTime() <= date.getTime()
  }

  //Two possible scenarios - 1) the payroll window has synced and the date is past
  // the payroll window or 2) it has not synced, but the current date is past the payroll
  //This function takes in a date in this case just the timesheet date) and
  //determines if it is out of the payroll window
  const pastPayroll = (date: Date) => {
    const now = new Date()
    if (currentPayrollWindow.value) {
      //check if we have elapsed the current payroll window on the device (this would
      //be true before a sync occurs
      if (now.getTime() > new Date(currentPayrollWindow.value.end).getTime()) {
        return date.getTime() < new Date(currentPayrollWindow.value.end).getTime()
      } else {
        //else we are within current payroll window
        return date.getTime() < currentPayrollWindow.value.start
      }
    }
    return false
  }

  return {
    current,
    requestPayrollData,
    setPayroll,
    resetStoreState,
    withinCurrentPayPeriod,
    pastPayrollEnd,
    pastPayroll,
  }
})
